import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Warning } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Logo } from "../components/Logo";
import { useI18n } from "../lib/i18n";

const STEP_KEYS = ["proc.s1", "proc.s2", "proc.s3", "proc.s4", "proc.s5", "proc.s6", "proc.s7"];

export default function Processing() {
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const file = location.state?.file;
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(6);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const startedRef = useRef(false);

  // Kick off the real analysis exactly once (StrictMode-safe via ref guard).
  useEffect(() => {
    if (!file) {
      navigate("/", { replace: true });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;

    const form = new FormData();
    form.append("file", file);
    const model = localStorage.getItem("lexadm_model") || "gpt-5.4";
    api
      .post(`/analyze?model=${model}`, form, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => setResult(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [file, navigate]);

  // Animated checklist — independent of the kickoff guard so it always runs.
  useEffect(() => {
    if (error) return;
    const id = setInterval(() => {
      setStep((s) => {
        const ns = Math.min(STEP_KEYS.length, s + 1);
        setProgress(Math.min(92, 6 + ns * 12));
        return ns;
      });
    }, 650);
    return () => clearInterval(id);
  }, [error]);

  // Navigate once analysis is ready and the checklist has finished.
  useEffect(() => {
    if (error || !result) return;
    if (step >= STEP_KEYS.length) {
      setProgress(100);
      const t = setTimeout(() => navigate(`/result/${result.analysis_id}`, { replace: true }), 400);
      return () => clearTimeout(t);
    }
  }, [step, result, error, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[#F8F9FA]">
      <div className="mb-10">
        <Logo variant="light" size="lg" />
      </div>

      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg p-8 shadow-[0_20px_40px_-16px_rgba(10,17,40,0.15)]">
        {error ? (
          <div className="text-center" data-testid="processing-error">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-risk-critical/10 text-risk-critical mb-4">
              <Warning size={28} weight="fill" />
            </span>
            <p className="text-navy font-semibold">{error}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 bg-navy text-white text-sm font-semibold rounded-md px-5 py-2.5 hover:bg-black transition-colors"
            >
              {t("viewer.back")}
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-5 h-5 rounded-full border-2 border-slate-200 border-t-navy animate-spin-slow" />
              <h2 className="font-display font-semibold text-xl text-navy" data-testid="processing-title">
                {t("proc.title")}
              </h2>
            </div>

            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-navy rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="progress-bar"
              />
            </div>

            <ul className="mt-7 space-y-3.5">
              {STEP_KEYS.map((key, i) => {
                const done = i < step;
                const active = i === step;
                return (
                  <li key={key} className="flex items-center gap-3">
                    <span
                      className={`flex items-center justify-center w-6 h-6 rounded-full shrink-0 transition-colors duration-300 ${
                        done
                          ? "bg-risk-low text-white"
                          : active
                          ? "bg-gold/20 text-gold"
                          : "bg-slate-100 text-slate-300"
                      }`}
                    >
                      {done ? (
                        <span className="animate-check-pop">
                          <Check size={14} weight="bold" />
                        </span>
                      ) : active ? (
                        <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-slate-300" />
                      )}
                    </span>
                    <span
                      className={`text-sm transition-colors duration-300 ${
                        done ? "text-navy font-medium" : active ? "text-navy" : "text-slate-400"
                      }`}
                    >
                      {t(key)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
